import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { ChevronDown, ExternalLink } from "lucide-react";
import { ClientsAccordion } from "@/components/ClientsAccordion";
import {
  APP_FEATURES,
  INVESTMENTS,
  EVENTBRITE_URL,
} from "@/data/clients";
import { INVESTMENT_LOGOS } from "@/data/logos";
import { PikWordmark } from "@/components/PikWordmark";
import techfast from "@/assets/techfast.png";
import pikLogo from "@/assets/logos/peopleiknow.png";
import pikHome from "@/assets/pik-home.png";
import pikCard from "@/assets/pik-card.png";
import pikQr from "@/assets/pik-qr.png";

interface Activity {
  id: string;
  title: string;
  tagline: string;
  bgVar: string;
}

const ACTIVITIES: Activity[] = [
  {
    id: "consulting",
    title: "Consulting",
    tagline:
      "Go-to-market strategy, sales leadership, executive positioning, and deal execution for startups and growth-stage technology companies.",
    bgVar: "var(--section-consulting)",
  },
  {
    id: "apps",
    title: "Apps",
    tagline:
      "Building useful and interesting software products, increasingly powered by AI.",
    bgVar: "var(--section-apps)",
  },
  {
    id: "events",
    title: "Events",
    tagline:
      "Creating community-oriented technology events, including the Stampede Parade Techfast.",
    bgVar: "var(--section-events)",
  },
  {
    id: "investments",
    title: "Investments",
    tagline:
      "Backing promising early-stage technology companies, with a particular interest in Alberta-based founders and businesses.",
    bgVar: "var(--section-investments)",
  },
];

const LINK_CLS =
  "font-medium text-[#0F0750] underline decoration-[#B9C4F2] underline-offset-4 transition-colors hover:text-[#3B5BDB]";

const AppsBody = () => (
  <div className="space-y-5">
    <p className="text-sm leading-relaxed text-slate-600 sm:text-base">
      Foosball Apps uses AI to build interesting projects. Its main focus
      presently is{" "}
      <a
        data-testid="apps-peopleiknow-link"
        href="https://peopleiknow.ai"
        target="_blank"
        rel="noopener noreferrer"
        className={LINK_CLS}
      >
        <PikWordmark />
      </a>
      .
    </p>
    <div className="flex items-center gap-3">
      <img
        data-testid="apps-peopleiknow-logo"
        src={pikLogo}
        alt="PeopleiKnow logo"
        className="h-9 w-auto object-contain"
      />
      <p className="font-heading text-base font-semibold text-[#0F0750] sm:text-lg">
        Your contacts app remembers phone numbers. PeopleiKnow remembers
        people.
      </p>
    </div>
    <div
      data-testid="apps-screenshots"
      className="flex flex-wrap items-start gap-4"
    >
      {[
        { src: pikHome, alt: "PeopleiKnow home screen", id: "home" },
        { src: pikCard, alt: "PeopleiKnow digital business card", id: "card" },
        { src: pikQr, alt: "PeopleiKnow QR intro screen", id: "qr" },
      ].map((s) => (
        <img
          key={s.id}
          data-testid={`apps-screenshot-${s.id}`}
          src={s.src}
          alt={s.alt}
          className="h-60 w-auto drop-shadow-lg sm:h-72"
        />
      ))}
    </div>
    <AccordionPrimitive.Root
      type="single"
      collapsible
      className="w-full"
      data-testid="apps-features-accordion"
    >
      {APP_FEATURES.map((f) => (
        <AccordionPrimitive.Item
          key={f.id}
          value={f.id}
          data-testid={`apps-feature-item-${f.id}`}
          className="border-b border-slate-200"
        >
          <AccordionPrimitive.Header className="flex">
            <AccordionPrimitive.Trigger
              data-testid={`apps-feature-trigger-${f.id}`}
              className="group flex flex-1 items-center justify-between py-4 text-left font-heading text-sm font-semibold tracking-tight text-slate-800 transition-colors hover:text-[#3B5BDB] md:text-base"
            >
              {f.title}
              <ChevronDown className="h-4 w-4 shrink-0 text-[#0F0750] transition-transform duration-300 group-data-[state=open]:rotate-180" />
            </AccordionPrimitive.Trigger>
          </AccordionPrimitive.Header>
          <AccordionPrimitive.Content
            data-testid={`apps-feature-content-${f.id}`}
            className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
          >
            <p className="pb-5 text-sm leading-relaxed text-slate-600 sm:text-base">
              {f.body}
            </p>
          </AccordionPrimitive.Content>
        </AccordionPrimitive.Item>
      ))}
    </AccordionPrimitive.Root>
  </div>
);

const EventsBody = () => (
  <div className="space-y-5">
    <p className="text-sm leading-relaxed text-slate-600 sm:text-base">
      For over 20 years, I have been involved in running what is now called
      the &ldquo;Stampede Parade Techfast&rdquo;. Arguably the premier Stampede
      event for technology vendors and customers, it brings together over 500
      people in a family friendly event with parade-route seating, food,
      beverage &amp; good people.
    </p>
    <img
      data-testid="events-techfast-image"
      src={techfast}
      alt="Stampede Parade Techfast 2026 event artwork"
      className="w-full max-w-xl object-contain drop-shadow-sm"
    />
    <p className="text-sm text-slate-500">
      Stampede Parade Techfast 2026 — Friday, July 3, 8:30&ndash;11:30 AM at
      Garage Sports Bar, Calgary, AB.
    </p>
    <a
      data-testid="events-eventbrite-link"
      href={EVENTBRITE_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-2 font-heading text-sm font-semibold ${LINK_CLS}`}
    >
      View on Eventbrite
      <ExternalLink className="h-3.5 w-3.5" />
    </a>
  </div>
);

const InvestmentsBody = () => (
  <div className="space-y-4">
    <p className="text-sm leading-relaxed text-slate-600 sm:text-base">
      Active investor and advisor for early stage start ups, mostly tech based
      in Alberta. Including:
    </p>
    <ul className="space-y-4">
      {INVESTMENTS.map((inv) => (
        <li key={inv.name} className="flex items-start gap-3">
          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#0F0750]" />
          <div>
            <span className="flex items-center gap-2.5">
              <img
                src={INVESTMENT_LOGOS[inv.name]}
                alt={`${inv.name} logo`}
                className="h-6 w-auto max-w-[110px] object-contain"
              />
              <a
                data-testid={`investment-link-${inv.name
                  .toLowerCase()
                  .replace(/\s+/g, "-")}`}
                href={inv.href}
                target="_blank"
                rel="noopener noreferrer"
                className={`font-heading text-sm font-semibold md:text-base ${LINK_CLS}`}
              >
                {inv.name}
              </a>
            </span>
            <p className="mt-0.5 text-sm leading-relaxed text-slate-600">
              {inv.description}
            </p>
          </div>
        </li>
      ))}
    </ul>
  </div>
);

const ConsultingBody = () => (
  <div className="space-y-4">
    <p className="text-sm leading-relaxed text-slate-600 sm:text-base">
      Foosball Consulting helps commercialize start ups, build their
      go-to-market strategy, and execute on deals to drive revenue. I offer
      sales and sales leadership while supporting the development of sales
      process &amp; operations that can help you successfully scale.
    </p>
    <ClientsAccordion />
  </div>
);

const BODIES: Record<string, () => React.ReactElement> = {
  consulting: ConsultingBody,
  apps: AppsBody,
  events: EventsBody,
  investments: InvestmentsBody,
};

export const ActivitiesAccordion = () => (
  <AccordionPrimitive.Root
    type="single"
    collapsible
    className="w-full"
    data-testid="activities-accordion"
  >
    {ACTIVITIES.map((a) => {
      const Body = BODIES[a.id];
      return (
        <AccordionPrimitive.Item
          key={a.id}
          value={a.id}
          data-testid={`activity-item-${a.id}`}
          className="mb-4 rounded-xl px-5 transition-shadow duration-300 data-[state=open]:shadow-md sm:px-7"
          style={{ backgroundColor: a.bgVar }}
        >
          <AccordionPrimitive.Header className="flex">
            <AccordionPrimitive.Trigger
              data-testid={`activity-trigger-${a.id}`}
              className="group flex flex-1 items-center justify-between gap-4 py-6 text-left md:py-7"
            >
              <span>
                <span className="block font-heading text-xl font-semibold tracking-tight text-[#0F0750] transition-transform duration-300 group-hover:translate-x-1.5 md:text-2xl">
                  {a.title}
                </span>
                <span className="mt-1 block text-sm text-slate-500">
                  {a.tagline}
                </span>
              </span>
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-300 text-[#0F0750] transition-colors duration-300 group-hover:bg-[#0F0750] group-hover:text-white group-data-[state=open]:bg-[#0F0750] group-data-[state=open]:text-white">
                <ChevronDown className="h-4 w-4 transition-transform duration-300 group-data-[state=open]:rotate-180" />
              </span>
            </AccordionPrimitive.Trigger>
          </AccordionPrimitive.Header>
          <AccordionPrimitive.Content
            data-testid={`activity-content-${a.id}`}
            className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
          >
            <div className="pb-8 pt-1">
              <Body />
            </div>
          </AccordionPrimitive.Content>
        </AccordionPrimitive.Item>
      );
    })}
  </AccordionPrimitive.Root>
);
